(() => {
  const roots = document.querySelectorAll('[data-gx91-ai]');
  if (!roots.length) return;

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));
  const track = (event, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...details });
  };

  roots.forEach((root) => {
    if (root.dataset.gx91Ready === 'true') return;
    root.dataset.gx91Ready = 'true';
    const launcher = root.querySelector('[data-gx91-open]');
    const panel = root.querySelector('[data-gx91-panel]');
    const closeButton = root.querySelector('[data-gx91-close]');
    const messages = root.querySelector('[data-gx91-messages]');
    const form = root.querySelector('[data-gx91-form]');
    const input = root.querySelector('[data-gx91-input]');
    const upload = root.querySelector('[data-gx91-upload]');
    const artworkCard = root.querySelector('[data-gx91-artwork]');
    const artworkPreview = root.querySelector('[data-gx91-artwork-preview]');
    const artworkName = root.querySelector('[data-gx91-artwork-name]');
    const artworkRemove = root.querySelector('[data-gx91-artwork-remove]');
    let lastFocus = null;
    let context = { quantity: 0, budget: 0, query: 'gift', corporate: false, premium: false, lastPrompt: '', products: [] };
    let transferredArtwork = null;
    let activeRequest = null;
    let requestSequence = 0;
    const submitButton = form.querySelector('button[type="submit"]');

    const open = () => {
      lastFocus = document.activeElement;
      panel.hidden = false;
      launcher.setAttribute('aria-expanded', 'true');
      root.classList.add('is-open');
      window.setTimeout(() => input.focus(), 80);
      track('gifty_ai_open', { version: '12' });
    };
    const close = () => {
      panel.hidden = true;
      launcher.setAttribute('aria-expanded', 'false');
      root.classList.remove('is-open');
      lastFocus?.focus?.();
    };

    const addMessage = (role, html, extra = '') => {
      const item = document.createElement('div');
      item.className = `gx91-ai__message gx91-ai__message--${role} ${extra}`.trim();
      item.innerHTML = html;
      messages.append(item);
      messages.scrollTop = messages.scrollHeight;
      return item;
    };

    const parseRequest = (raw) => {
      const lower = raw.toLowerCase().replace(/\u00a0/g, ' ');
      const greeting = /^(hi|hello|hey|good\s+(morning|afternoon|evening)|سلام|درود)[!. ]*$/i.test(raw.trim());
      const asksCustomization = /(customiz|personaliz|logo|artwork|mockup|proof|print|embroider|brand)/.test(lower);
      const asksShipping = /(ship|deliver|vancouver|canada|turnaround|deadline|arrive)/.test(lower);
      const asksColourAdvice = /(which|what|try|best|recommend).{0,28}(colou?r|contrast|white logo|black logo|gold)/.test(lower);
      const moneyMatch = lower.match(/(?:\bcad\s*|\$)\s*([\d,]+(?:\.\d{1,2})?)/)
        || lower.match(/(?:under|up to|max(?:imum)?|budget(?:\s+(?:is|of))?|spend(?:ing)?(?:\s+up\s+to)?)\s*(?:about|around|total)?\s*(?:cad\s*|\$)?\s*([\d,]+(?:\.\d{1,2})?)/);
      const budget = moneyMatch ? Number(moneyMatch[1].replaceAll(',', '')) : 0;
      const labelledQuantity = lower.match(/\b([\d,]+)\s*(?:units?|pieces?|items?|employees?|people|persons?|clients?|gifts?|staff|team\s+members?|attendees?)\b/)
        || lower.match(/\b(?:need|order|buy|for|want)\s+(?:about|around|approximately)?\s*([\d,]+)\b/);
      const parsedQuantity = labelledQuantity ? Number(labelledQuantity[1].replaceAll(',', '')) : 0;
      const quantity = parsedQuantity >= 1900 && parsedQuantity <= 2100 ? 0 : parsedQuantity;
      const corporate = quantity >= 10 || /(corporate|employee|client|company|business|staff|team|office|bulk|branded|logo|promotional|conference)/.test(lower);
      const premium = /(premium|executive|luxury|high[- ]end|impressive|vip)/.test(lower) || context.premium;
      let query = 'gift';
      if (/(christmas|holiday)/.test(lower)) query = corporate ? 'corporate christmas gift' : 'christmas gift';
      else if (/(birthday)/.test(lower)) query = 'birthday gift';
      else if (/(anniversary|wedding|engagement|couple)/.test(lower)) query = 'anniversary wedding gift';
      else if (/(mug|cup|drinkware|bottle)/.test(lower)) query = 'custom mug drinkware';
      else if (/(pen|notebook|desk|office)/.test(lower)) query = 'executive desk gift';
      else if (/(wine|barware|flask)/.test(lower)) query = 'wine barware gift';
      else if (/(apparel|shirt|hoodie|cap|hat)/.test(lower)) query = 'custom apparel';
      else if (/(custom|personalized|logo|branded)/.test(lower)) query = 'custom logo gift';
      else if (corporate) query = 'corporate gift';
      const mergedQuantity = quantity || context.quantity;
      const mergedBudget = budget || context.budget;
      const totalBudget = budget && mergedQuantity >= 2 && /(total|budget|have|spend)/.test(lower) && !/(each|per|unit|under|up to)/.test(lower);
      const perUnitBudget = totalBudget ? Math.max(1, Math.floor(budget / mergedQuantity)) : mergedBudget;
      return { raw, lower, greeting, asksCustomization, asksShipping, asksColourAdvice, budget: perUnitBudget, statedBudget: budget, totalBudget, quantity: mergedQuantity, corporate: corporate || context.corporate, premium, query: query === 'gift' && context.query !== 'gift' ? context.query : query };
    };

    const numericPrice = (product) => {
      const raw = product.price_min ?? product.price ?? product.price_max ?? '';
      const number = Number(String(raw).replace(/[^0-9.]/g, ''));
      return Number.isFinite(number) ? number : 0;
    };
    const displayPrice = (product) => {
      if (typeof product.price === 'string' && /[$€£]/.test(product.price)) return product.price;
      const number = numericPrice(product);
      return number ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(number) : 'View price';
    };
    const formatCad = (number) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(number);
    const volumeMultiplier = (quantity) => quantity >= 100 ? .78 : quantity >= 50 ? .82 : quantity >= 25 ? .88 : quantity >= 10 ? .92 : 1;

    const searchCatalog = async (request, signal) => {
      const url = new URL('/search/suggest.json', window.location.origin);
      url.searchParams.set('q', request.query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('Catalog search unavailable');
      const data = await response.json();
      let products = (data?.resources?.results?.products || []).map((product) => {
        const basePrice = numericPrice(product);
        const estimatedUnitPrice = basePrice ? Number((basePrice * volumeMultiplier(request.quantity)).toFixed(2)) : 0;
        return { ...product, _gxBasePrice: basePrice, _gxEstimatedUnitPrice: estimatedUnitPrice };
      });
      if (request.budget) {
        const withinBudget = products.filter((product) => {
          const price = product._gxEstimatedUnitPrice || numericPrice(product);
          return !price || price <= request.budget;
        });
        products = withinBudget;
      }
      return products.slice(0, 3);
    };

    const productMarkup = (product, request) => {
      const url = product.url || `/products/${product.handle}`;
      const customizable = true;
      const designUrl = new URL(url, window.location.origin);
      if (customizable) {
        designUrl.searchParams.set('open_customizer', '1');
        designUrl.searchParams.set('gift_brief', request.raw.slice(0, 220));
      }
      const rawImage = product.image || product.featured_image || '';
      const image = typeof rawImage === 'string' ? rawImage : (rawImage.url || rawImage.src || '');
      const estimated = request.quantity >= 10 && product._gxEstimatedUnitPrice
        ? `<span>Estimated ${escapeHtml(formatCad(product._gxEstimatedUnitPrice))} each at ${escapeHtml(request.quantity)} · before customization</span>`
        : `<span>${escapeHtml(displayPrice(product))}</span>`;
      return `<article class="gx91-ai__result">
        <a class="gx91-ai__result-image" href="${escapeHtml(url)}">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<span>✦</span>'}</a>
        <div><a href="${escapeHtml(url)}"><strong>${escapeHtml(product.title)}</strong></a>${estimated}</div>
        <a class="gx91-ai__result-action" href="${escapeHtml(customizable ? designUrl.pathname + designUrl.search : url)}" data-gx91-result="${escapeHtml(product.title)}">${transferredArtwork ? 'Preview my logo' : 'Design it'} →</a>
      </article>`;
    };

    const answer = async (raw) => {
      const value = String(raw || '').trim();
      if (!value) return;
      addMessage('user', `<p>${escapeHtml(value)}</p>`);
      input.value = '';
      const request = parseRequest(value);
      context = { ...context, quantity: request.quantity, budget: request.budget, query: request.query, corporate: request.corporate, premium: request.premium, lastPrompt: value };
      track('gifty_ai_conversation', { version: '12', prompt_length: value.length, corporate: request.corporate, has_artwork: !!transferredArtwork });
      if (request.greeting) {
        addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>Hi! 👋 I can help you choose a product, work within a total or per-unit budget, plan a bulk order, or move your logo into the Design Studio. Who is the gift for?</p>');
        return;
      }
      if (request.asksCustomization && /(how|process|work|proof|production)/.test(request.lower) && !/(mug|bottle|hoodie|shirt|box|notebook|pen)/.test(request.lower)) {
        addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>Choose a product, open Design Studio, upload your artwork and position it inside the preview area. Save the mockup, then send it for an exact quote. The on-screen mockup is a visual estimate; Gifty confirms the printable area, method, MOQ and final proof before production.</p><a class="gx91-ai__quote" href="/pages/how-it-works">See the complete customization process →</a>');
        return;
      }
      if (request.asksShipping && !/(gift|mug|bottle|hoodie|shirt|box|notebook|pen|product)/.test(request.lower)) {
        addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>Gifty supports Vancouver-area customers and ships across Canada. Standard Canadian shipping is $16.99 below $250 and free from $250 before tax. Custom-product production timing is confirmed after artwork review.</p><a class="gx91-ai__quote" href="/pages/contact">Confirm a deadline with Gifty →</a>');
        return;
      }
      if (request.asksColourAdvice) {
        const artworkMessage = transferredArtwork
          ? 'Your uploaded logo is available for an approximate product preview. High-contrast black or white artwork is normally the safest first test; exact colour matching depends on the product material and production method.'
          : 'Upload your logo so the Design Studio can test black, white, monochrome and original-colour previews. Exact colour matching still requires Gifty artwork review and a production proof.';
        addMessage('assistant', `<span class="gx91-ai__label">Gifty AI · artwork guidance</span><p>${artworkMessage}</p><a class="gx91-ai__quote" href="/pages/how-it-works">See how artwork approval works →</a>`);
        return;
      }

      activeRequest?.abort();
      activeRequest = new AbortController();
      const sequence = ++requestSequence;
      if (submitButton) submitButton.disabled = true;
      const typing = addMessage('assistant', '<span class="gx91-ai__typing" aria-label="Searching the live catalog"><i></i><i></i><i></i></span>', 'is-typing');
      try {
        const products = await searchCatalog(request, activeRequest.signal);
        if (sequence !== requestSequence) return;
        context.products = products;
        typing.remove();
        if (!products.length) {
          const noMatchCopy = request.budget
            ? `I didn’t find a confident live-catalog match at or below ${escapeHtml(formatCad(request.budget))} per unit for this brief. I’m not showing over-budget products as if they fit. Gifty can check alternate products, quantities and branding methods.`
            : 'I couldn’t find a confident live-product match for that exact brief. Try one broader detail, or let the Gifty team build a shortlist.';
          addMessage('assistant', `<span class="gx91-ai__label">Gifty AI</span><p>${noMatchCopy}</p><a class="gx91-ai__quote" href="/pages/contact?brief=${encodeURIComponent(value)}">Request a curated shortlist →</a>`);
          return;
        }
        const facts = [request.quantity ? `${request.quantity.toLocaleString('en-CA')} pieces` : '', request.budget ? `up to ${formatCad(request.budget)} each` : '', request.premium ? 'premium direction' : ''].filter(Boolean).join(' · ');
        const budgetNote = request.totalBudget ? `<p class="gx91-ai__budget-note">I treated your ${escapeHtml(formatCad(request.statedBudget))} as a total budget: about ${escapeHtml(formatCad(request.budget))} per person before tax, shipping and customization.</p>` : '';
        const artworkNote = transferredArtwork ? `<p class="gx91-ai__budget-note">Your uploaded logo is ready. Choose “Preview my logo” to transfer it into that product’s Design Studio.</p>` : '';
        const timing = /(by|before|deadline|deliver|date|tomorrow|week)/.test(request.lower)
          ? '<p class="gx91-ai__warning">I found product matches, but delivery timing still needs confirmation from Gifty.</p>' : '';
        addMessage('assistant', `<span class="gx91-ai__label">Gifty AI · live results</span><p>${request.corporate ? 'These are the strongest scalable matches I found in the current catalog.' : 'These are the strongest live matches I found.'}${facts ? ` I noted ${escapeHtml(facts)}.` : ''}</p>${budgetNote}${artworkNote}<div class="gx91-ai__results">${products.map((product) => productMarkup(product, request)).join('')}</div>${timing}${request.corporate ? `<a class="gx91-ai__quote" href="/pages/contact?brief=${encodeURIComponent(value)}">Need branding or a large quantity? Request an exact quote →</a>` : ''}<p class="gx91-ai__followup">You can keep talking naturally—try “make it more premium”, “show bottles instead”, or “my total budget is $2,500”.</p>`);
      } catch (error) {
        typing.remove();
        if (error?.name !== 'AbortError') addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>The live catalog search is temporarily unavailable. Your storefront is still working normally.</p><a class="gx91-ai__quote" href="/pages/gift-finder">Open the Gift Finder →</a>');
      } finally {
        if (sequence === requestSequence && submitButton) submitButton.disabled = false;
      }
    };

    const showArtwork = (artwork) => {
      transferredArtwork = artwork;
      artworkCard.hidden = !artwork;
      if (!artwork) return;
      artworkPreview.src = artwork.data;
      artworkName.textContent = artwork.name;
    };
    try {
      const savedArtwork = JSON.parse(localStorage.getItem('gifty-ai-transfer') || 'null');
      if (savedArtwork?.data && Date.now() - Number(savedArtwork.savedAt || 0) < 60 * 60 * 1000) showArtwork(savedArtwork);
    } catch (_) { /* Artwork transfer is optional. */ }

    upload.addEventListener('change', () => {
      const file = upload.files?.[0];
      if (!file) return;
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowed.includes(file.type)) { addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>Please upload a PNG, JPG or SVG logo.</p>'); upload.value = ''; return; }
      if (file.size > 1.5 * 1024 * 1024) { addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>That file is larger than 1.5 MB. Please upload a smaller preview logo; original production artwork can be supplied with the exact quote.</p>'); upload.value = ''; return; }
      const reader = new FileReader();
      reader.onload = () => {
        if (file.type === 'image/svg+xml') {
          try {
            const encoded = String(reader.result).split(',')[1] || '';
            const svgText = atob(encoded);
            if (/<script|<foreignObject|\son\w+\s*=|(?:href|src)\s*=\s*["'](?:https?:|data:text\/html)/i.test(svgText)) {
              addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>This SVG contains active or externally loaded content and cannot be previewed safely. Export a clean SVG or PNG instead.</p>');
              upload.value = '';
              return;
            }
          } catch (_) {
            addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>This SVG could not be validated safely. Export it as a clean SVG or PNG.</p>');
            upload.value = '';
            return;
          }
        }
        const artwork = { name: file.name, type: file.type, data: reader.result, savedAt: Date.now(), quality: file.type === 'image/svg+xml' ? 'SVG logo · production review required' : 'Logo uploaded in Gifty assistant · resolution reviewed in Design Studio' };
        try { localStorage.setItem('gifty-ai-transfer', JSON.stringify(artwork)); } catch (_) { addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>Your browser could not store this logo locally. You can still upload it directly inside Design Studio.</p>'); return; }
        showArtwork(artwork);
        addMessage('assistant', `<span class="gx91-ai__label">Gifty AI</span><p>I have your logo ready in this browser. Tell me what product you want—or choose a result and I’ll transfer the logo into Design Studio for an approximate mockup.</p>`);
        track('gifty_ai_artwork_uploaded', { version: '12', file_type: file.type, file_size: file.size });
      };
      reader.readAsDataURL(file);
    });
    artworkRemove.addEventListener('click', () => {
      transferredArtwork = null; upload.value = ''; artworkCard.hidden = true; artworkPreview.removeAttribute('src');
      try { localStorage.removeItem('gifty-ai-transfer'); } catch (_) {}
      addMessage('assistant', '<span class="gx91-ai__label">Gifty AI</span><p>Logo removed from this browser.</p>');
    });

    launcher.addEventListener('click', open);
    closeButton.addEventListener('click', close);
    form.addEventListener('submit', (event) => { event.preventDefault(); answer(input.value); });
    root.querySelectorAll('[data-gx91-prompt]').forEach((button) => button.addEventListener('click', () => answer(button.dataset.gx91Prompt)));
    root.addEventListener('click', (event) => {
      const result = event.target.closest('[data-gx91-result]');
      if (result) track('gifty_ai_result_click', { version: '12', result: result.dataset.gx91Result, has_artwork: !!transferredArtwork });
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !panel.hidden) close(); });
  });
})();
